import React, { useCallback, useMemo } from 'react';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import {
  buildDateRangeFilterChip,
  buildOptionFilterChips,
  buildSingleValueFilterChip,
  compactFilterChips,
} from '../../components/common/FilterChipsBar/buildFilterChips';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import SingleSelectFilter from '../../components/common/SingleSelectFilter/SingleSelectFilter';
import {
  COMMERCIAL_BOOLEAN_FILTER_OPTIONS,
  ONBOARDING_CLASSIFICATION_FILTER_OPTIONS,
  type CommercialBooleanFilterValue,
} from '../../constants/commercialFilters';
import {
  COMMERCIAL_JOINED_AT_DATE_PRESETS,
  detectDatePreset,
  formatDatePresetShort,
} from '../../constants/datePresets';
import type { QueryFilterValues } from '../../hooks/useQueryFilters';
import { useMarketingProjects } from '../../hooks/useMarketingProjects';
import { ONBOARDING_QUERY_FILTER_SCHEMA } from './onboardingQueryFilters';
import styles from './Onboarding.module.css';

type OnboardingQueryFilters = QueryFilterValues<typeof ONBOARDING_QUERY_FILTER_SCHEMA>;
type OnboardingFilterKey = Exclude<keyof OnboardingQueryFilters, 'page'>;

interface OnboardingFiltersProps {
  filters: OnboardingQueryFilters;
  loading: boolean;
  totalRecords: number;
  hasActiveFilters: boolean;
  setFilter: <K extends OnboardingFilterKey>(
    key: K,
    value: OnboardingQueryFilters[K] | ((current: OnboardingQueryFilters[K]) => OnboardingQueryFilters[K]),
  ) => void;
  setFilters: (patch: Partial<OnboardingQueryFilters>) => void;
  onClearFilters: () => void;
}

const DATE_PAGAMENTO_PLACEHOLDER = 'Data pagamento';

function formatPaidAtChipLabel(start: string, end: string): string {
  const preset = detectDatePreset(start, end, COMMERCIAL_JOINED_AT_DATE_PRESETS);
  const presetOption = COMMERCIAL_JOINED_AT_DATE_PRESETS.find((option) => option.value === preset);
  const selectionLabel = presetOption && preset !== 'custom'
    ? presetOption.label
    : `${formatDatePresetShort(start)} – ${formatDatePresetShort(end)}`;

  return `${DATE_PAGAMENTO_PLACEHOLDER}: ${selectionLabel}`;
}

function removeMultiValue(values: string[], valueToRemove: string): string[] {
  return values.filter((value) => value !== valueToRemove);
}

const OnboardingFilters: React.FC<OnboardingFiltersProps> = ({
  filters,
  loading,
  totalRecords,
  hasActiveFilters,
  setFilter,
  setFilters,
  onClearFilters,
}) => {
  const {
    searchName,
    classifications,
    projectCodes,
    hasPhotos,
    hasParq,
    hasAnamnesis,
    joinedAtStart,
    joinedAtEnd,
  } = filters;
  const projects = useMarketingProjects();
  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );

  const handleJoinedAtChange = useCallback((start: string, end: string) => {
    setFilters({ joinedAtStart: start, joinedAtEnd: end });
  }, [setFilters]);

  const filterChips: FilterChipEntry[] = useMemo(() => compactFilterChips(
    buildDateRangeFilterChip({
      filterKey: 'joinedAt',
      start: joinedAtStart,
      end: joinedAtEnd,
      label: formatPaidAtChipLabel(joinedAtStart, joinedAtEnd),
      onRemove: () => setFilters({ joinedAtStart: '', joinedAtEnd: '' }),
    }),
    buildOptionFilterChips({
      filterKey: 'projectCodes',
      values: projectCodes,
      options: projectFilterOptions,
      labelPrefix: 'Projeto',
      onRemove: (value) => setFilter(
        'projectCodes',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'classifications',
      values: classifications,
      options: ONBOARDING_CLASSIFICATION_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'classifications',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildSingleValueFilterChip({
      filterKey: 'hasAnamnesis',
      value: hasAnamnesis,
      options: COMMERCIAL_BOOLEAN_FILTER_OPTIONS,
      labelPrefix: 'Tem anamnese',
      onRemove: () => setFilter('hasAnamnesis', ''),
    }),
    buildSingleValueFilterChip({
      filterKey: 'hasParq',
      value: hasParq,
      options: COMMERCIAL_BOOLEAN_FILTER_OPTIONS,
      labelPrefix: 'Tem PAR-Q',
      onRemove: () => setFilter('hasParq', ''),
    }),
    buildSingleValueFilterChip({
      filterKey: 'hasPhotos',
      value: hasPhotos,
      options: COMMERCIAL_BOOLEAN_FILTER_OPTIONS,
      labelPrefix: 'Tem fotos',
      onRemove: () => setFilter('hasPhotos', ''),
    }),
  ), [
    projectCodes,
    projectFilterOptions,
    classifications,
    hasAnamnesis,
    hasParq,
    hasPhotos,
    joinedAtEnd,
    joinedAtStart,
    setFilter,
    setFilters,
  ]);

  return (
    <>
      <FilterBar gap="sm" pushFirstChild className={styles['filter-bar']}>
        <RecordCount
          label="Alunas:"
          count={totalRecords}
          className={styles['record-count']}
        />
        <FilterGroup className={styles['search-filter-group']}>
          <SearchFilterBar
            searchValue={searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={joinedAtStart}
            dateEnd={joinedAtEnd}
            onChange={handleJoinedAtChange}
            placeholder={DATE_PAGAMENTO_PLACEHOLDER}
            presetOptions={COMMERCIAL_JOINED_AT_DATE_PRESETS}
            allowEmpty
            keepPlaceholder
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            placeholder="Tag"
            clearButtonLabel="Limpar tags"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
            groups={[
              ...(projectFilterOptions.length > 0
                ? [{
                    title: 'Projeto',
                    values: projectCodes,
                    onChange: (values: string[]) => setFilter('projectCodes', values),
                    options: projectFilterOptions,
                  }]
                : []),
              {
                title: 'Jornada',
                values: classifications,
                onChange: (values: string[]) => setFilter('classifications', values),
                options: ONBOARDING_CLASSIFICATION_FILTER_OPTIONS,
              },
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <SingleSelectFilter
            value={hasAnamnesis}
            onChange={(value) => setFilter('hasAnamnesis', value as CommercialBooleanFilterValue)}
            options={COMMERCIAL_BOOLEAN_FILTER_OPTIONS}
            placeholder="Tem anamnese"
            neutralValue=""
            keepPlaceholder
          />
        </FilterGroup>
        <FilterGroup>
          <SingleSelectFilter
            value={hasParq}
            onChange={(value) => setFilter('hasParq', value as CommercialBooleanFilterValue)}
            options={COMMERCIAL_BOOLEAN_FILTER_OPTIONS}
            placeholder="Tem PAR-Q"
            neutralValue=""
            keepPlaceholder
          />
        </FilterGroup>
        <FilterGroup>
          <SingleSelectFilter
            value={hasPhotos}
            onChange={(value) => setFilter('hasPhotos', value as CommercialBooleanFilterValue)}
            options={COMMERCIAL_BOOLEAN_FILTER_OPTIONS}
            placeholder="Tem fotos"
            neutralValue=""
            keepPlaceholder
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={onClearFilters} />
        )}
      </FilterBar>

      <FilterChipsBar chips={filterChips} />
    </>
  );
};

export default OnboardingFilters;
